'use client'

import { usePathname, useRouter } from '@mntn-dev/app-navigation'

import { useTranslation } from '@mntn-dev/i18n'
import {
  type NavigationIconItemProps,
  getTestProps,
  useOpenState,
} from '@mntn-dev/ui-components'

import { type AnyRoute, route } from '@mntn-dev/app-routing'
import Image from 'next/image'
import { usePersonImageUrlInterceptor } from '~/components/avatar/use-person-image-url-interceptor.tsx'
import { NextImage } from '~/components/image/next-image.tsx'
import { Notifications } from '~/components/notification/notifications.tsx'
import { usePermissions } from '~/hooks/secure/use-permissions'

import { getAssetUrl } from '@mntn-dev/app-assets'
import { useFlags } from '@mntn-dev/flags-client'
import { Navigation } from '@mntn-dev/ui-components'
import { organizationTypeBorderColorMap } from '~/components/avatar/helper.ts'
import { useNotificationsQuery } from '~/components/notification/hooks/use-notifications-query.ts'
import { useMe } from '~/hooks/secure/use-me.ts'

type NavigationItem = NavigationIconItemProps & {
  authorized: () => boolean
  unreadNotificationCount?: number
  onClick: () => void
  selected: (navigationItem: NavigationItem) => boolean
}

export const Navbar = () => {
  const { me } = useMe()
  const { t } = useTranslation('navbar')
  const router = useRouter()
  const currentPath = usePathname()
  const { hasPermission } = usePermissions()
  const { useCustomIntercomLauncher } = useFlags()

  const { person } = usePersonImageUrlInterceptor(me, {
    height: 116,
    width: 116,
  })

  const selectedRoute = (route: AnyRoute) => () =>
    route.isMatch(currentPath, false)

  const notificationsOpenState = useOpenState()
  const { unreadNotificationCount } = useNotificationsQuery({ enabled: true })

  const navigationMap: NavigationItem[] = [
    {
      iconName: 'nav-projects',
      title: t('projects'),
      onClick: () => {
        router.push(route('/dashboard'))
      },
      authorized: () => true,
      selected: selectedRoute(route('/dashboard')),
      dataTestId: 'main-app-navigation-bar-item-projects',
      dataTrackingId: 'main-app-navigation-bar-item-projects',
    },
    {
      iconName: 'nav-organizations',
      title: t('organizations'),
      onClick: () => {
        router.push(route('/account/organizations'))
      },
      authorized: () => hasPermission('customer-organization:administer'),
      selected: selectedRoute(route('/account/organizations')),
      dataTestId: 'main-app-navigation-bar-item-organizations',
      dataTrackingId: 'main-app-navigation-bar-item-organizations',
    },
    {
      iconName: 'nav-packages',
      title: t('packages'),
      onClick: () => {
        router.push(route('/packages'))
      },
      authorized: () => hasPermission('package:administer'),
      selected: selectedRoute(route('/packages')),
      dataTestId: 'main-app-navigation-bar-item-packages',
      dataTrackingId: 'main-app-navigation-bar-item-packages',
    },
  ]

  const handleLogoClick = () => {
    router.push(route('/projects'))
  }

  const handleAvatarClick = () => {
    router.push(route('/account/profile'))
  }

  return (
    <Navigation>
      <Navigation.Header>
        <button
          type="button"
          onClick={handleLogoClick}
          {...getTestProps({ dataTestId: 'main-app-navigation-bar-header' })}
        >
          <Image
            src={getAssetUrl('quickframe-logo-nav')}
            width={54}
            height={36}
            alt="QuickFrame"
            className="h-9 w-[54px]"
          />
        </button>
      </Navigation.Header>
      <Navigation.Main>
        <Navigation.Items>
          {navigationMap.map((itemProps) => {
            if (itemProps.authorized()) {
              return (
                <Navigation.IconItem key={itemProps.iconName} {...itemProps} />
              )
            }
          })}
        </Navigation.Items>
        <Navigation.Items>
          {useCustomIntercomLauncher && (
            <Navigation.ButtonItem
              id="intercom-launcher"
              iconLeft="QuestionMarkCircleIcon"
              iconFill="solid"
              size="sm"
            />
          )}
          <Navigation.IconItem
            key="nav-bell"
            {...{
              iconName: 'nav-bell',
              title: t('alerts'),
              onClick: notificationsOpenState.onToggle,
              authorized: () => true,
              selected: () => false,
              unreadNotificationCount,
              dataTestId: 'main-app-navigation-bar-item-notifications',
              dataTrackingId: 'main-app-navigation-bar-item-notifications',
            }}
          />
          <Navigation.AvatarItem
            dataTestId="main-app-navigation-bar-item-profile"
            borderColor={organizationTypeBorderColorMap[me.organizationType]}
            person={person}
            image={NextImage({ unoptimized: true })}
            onClick={handleAvatarClick}
            selected={selectedRoute(route('/account'))}
          />
        </Navigation.Items>
      </Navigation.Main>
      <Notifications {...notificationsOpenState} />
    </Navigation>
  )
}
